/* eslint-disable max-len */
import { cacheDecorator } from '@ds/swymkit/tools'

import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import { communityParser } from '~/entities/container/community/api/parser-community'
import type {
  CreateCommunityData,
  CreateCommunityRequestData,
  GetCommunityRequestData,
  HighlightContent,
  ICommunity,
  ICommunityLiteMarksResponse,
  ICommunityRequest,
  INonMemberPrivateCommunity,
  ITenantMarks,
  IUnfollowedCommunity,
  TermsOfUseInfos,
  UpdateCommunityRequestData,
} from '~/entities/container/community/interfaces/community'
import type { IIdeaStatus } from '~/entities/content/idea/interfaces/idea'
import type { IUser } from '~/entities/user/user/interfaces/user'

import { contentParser } from '~/features-shared/content/api/parser-contents'
import { extractContents } from '~/features-shared/content/api/parser-exalead'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { IDerivedTypeResponse } from '~/features-shared/content-derived-type/interfaces/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'

import type { ExaleadResponse, SwymResponse } from '~/interfaces/services-dto'

export function getCommunities(): Promise<SwymResponse<ICommunity[]>> {
  return swymFetch
    .get('/api/communities/lite')
    .json()
}

/**
 * Gets the data about the marks of the communities
 * @returns The marks response
 */
export function getCommunitiesMarks() {
  return swymFetch
    .get('/api/communities/lite/marks')
    .json<SwymResponse<ICommunityLiteMarksResponse>>()
}

/**
 * Get a single community
 * @param id the community id or uri
 * @param withTags true to get community tags, default false
 */
export function getCommunity(id: string, withTags = false) {
  return cacheDecorator(
    `get-community-${id}-${withTags}`,
    () => {
      return swymFetch
        .post('/api/community/get', { json: { params: { as_admin: false, id, return_6wtags: withTags } } })
        .json<SwymResponse<ICommunity>>()
        .then(communityParser)
        .catch((error) => {
          if (error.data && error.data.result)
          // for non member private communities we get partial community data, so parse it so as to have normalized attributes, and continue throwing
            error.data = communityParser(error.data)

          throw error
        })
    },
    5000,
  )
}

/**
 * Get community with tags
 * @param id the community id or uri
 */
export function getCommunityWithTags(id: string) {
  return getCommunity(id, true)
}

/**
 * Get community but do not trigger an error if the community is private or has Terms of Use to sign.
 * This is useful for the router
 * @param id the community id or uri
 */
export function getCommunityWhenNotMembers(id: string): Promise<INonMemberPrivateCommunity> {
  return cacheDecorator(
    `get-community-non-member-${id}`,
    () => {
      return getCommunity(id)
        .then(response => response.result)
        .catch((error) => {
          if (error?.data?.result)
            return error.data.result
          throw error
        })
    },
    1000,
  )
}

export function createCommunity(data: CreateCommunityData) {
  return swymFetch
    .post('/api/community/add', {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunity>>()
    .then(communityParser)
}

export function createCommunityRequest(data: CreateCommunityRequestData) {
  return swymFetch
    .post('/api/communityRequest/create', {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunityRequest>>()
}

/**
 * @method getCommunityRequestStatus
 * @param data {GetCommunityRequestData} - attributes to get community request data
 * {string} page - page number beginning at 1
 * {string} limit - limit data amount for each page
 * @returns community request results
 */
export function getCommunityRequestStatus(data: GetCommunityRequestData) {
  return swymFetch
    .post('/api/communityRequest/listbyuser', {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunityRequest[]>>()
}

/**
 * @method updateCommunityRequest
 * @param data - attributes to update community request
 *  {string} title: community title
 *  {string} benefits: community expected benefits
 *  {string} description: community description
 *  {string} intranet_access: internal user access right
 *  {string} extranet_access: external user access right
 *  {string} primary_community_owner_hash_key: community owner hashKey
 * @returns community request result
 */
export function updateCommunityRequest(data: UpdateCommunityRequestData) {
  return swymFetch
    .post('/api/communityRequest/update', {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunityRequest[]>>()
}

/**
 * Check community title uniqueness. Check if an existing community has already
 * the provided title.
 * @param title - Community title to check.
 * @returns TRUE if the title already exists else FALSE.
 */
export function checkCommunityTitleUniqueness(title?: string) {
  return swymFetch
    .post('/api/community/checktitleuniqueness', {
      json: {
        params: {
          title,
        },
      },
    })
    .json<SwymResponse<boolean>>()
}

/**
 * @method editCommunity
 * @param dataToUpdate dataToUpdate -attributes to update for the edit community
 * @returns result with the attributes updated
 */
export function editCommunity(dataToUpdate: object) {
  return swymFetch
    .post('/api/community/edit', {
      json: {
        params: dataToUpdate,
      },
    })
    .json<SwymResponse<ICommunity>>()
}
/**
 * @method highlightContent
 * @param content dataToUpdate -attributes to highlight the content in the community
 * @returns result with the highlighted content update updated
 */
export function highlightContent(content: HighlightContent) {
  return swymFetch
    .post('/api/community/highlight', {
      json: {
        params: content,
      },
    })
    .json<SwymResponse<ICommunity>>()
}

export function editIdeationStatus(data: object) {
  return swymFetch
    .post('/api/Ideation/updatestatus', {
      json: {
        params: data,
      },
    })
    .json<SwymResponse<ICommunity>>()
}

interface IForwardData {
  content: IContent | ITimeline
  destinationType: IContentModel
  destinationDerivedTypeId: string
  threadUri: SUBJECT_6W_URI
  publicationState: boolean
  targetPosition: number
  targetUri?: SUBJECT_6W_URI
}

export async function forwardToThread(data: IForwardData) {
  const typeName = data.destinationType === IContentModel.Question ? 'question' : data.destinationType
  let url = `/api/${typeName}/forward`

  // Case for derived type content
  if (data.destinationDerivedTypeId)
    url += `?derived_type_id=${data.destinationDerivedTypeId}&api_version=v2`

  return swymFetch
    .post(url, {
      json: {
        community_uri: data.threadUri,
        content_uri: data.content.subject_uri,
        forward_parameters: { wiki_target_position: data.targetPosition },
        ...(data.publicationState ? { publication_state: 'DRAFT' } : undefined),
        ...(typeName === IContentModel.Wiki && { target_uri: data.targetUri }),
        timeout: 30000,
      },
    })
    .json<SwymResponse<ISwymContent>>()
    .then(contentParser)
}

interface IOptionsMarkAsRead { messageId: string, communityUri: string }
function _markAsRead(options: IOptionsMarkAsRead[]) {
  return swymFetch
    .post('/api/community/timeline/marks', {
      json: {
        marks: options.map(el => ({ community_id: el.communityUri, message_id: el.messageId })),
      },
      keepalive: true, // This is essential to perform the request when the page is closed
    })
    .json<SwymResponse<Record<string, 'OK' | 'KO'>>>()
}

export const markAsRead = mutualizerDecorator<
  typeof _markAsRead,
  IOptionsMarkAsRead
>({
  fetch: _markAsRead,
  merge: bucket => bucket,
  resolve: ([response, [_, resolve]]) => resolve(response),
  options: {
    throttle: 10000,
    sendBeforeUnload: true,
    sanitizer(bucket, item) {
      const b = bucket.filter(([args, resolve]) => {
        if (args.communityUri === item[0].communityUri) {
        // if the bucket already have a mark for the same community, resolve it

          resolve({ result: { [args.communityUri]: 'OK' } })
          // and remove it to keep the latest mark only
          return false
        }
        return true
      })
      return b
    },
  },
})

export function markAllAsRead(context: 'favorite_communities' | 'communities') {
  return swymFetch
    .post(`/api/community/timeline/marks/all?context=${context}`)
    .json<SwymResponse<ITenantMarks>>()
}

export function unfollowCommunity(community_id: string) {
  return swymFetch
    .post('/api/userPreferences/unfollow', {
      json: {
        params: {
          id: community_id,
          model: 'Community',
        },
      },
    })
    .json<SwymResponse<{ success: boolean }>>()
}

export function followCommunity(community_id: string) {
  return swymFetch
    .post('/api/userPreferences/follow', {
      json: {
        params: {
          id: community_id,
          model: 'Community',
        },
      },
    })
    .json<SwymResponse<{ success: boolean }>>()
}

export function leaveCommunity(community_id: string) {
  return swymFetch
    .post('/api/community/deletercurrentuserfromcommunity', {
      json: { params: { community_id } },
    })
    .json<SwymResponse<null>>()
}

export function getIdeaStatuses(community_id: string, derived_type_id?: string) {
  return cacheDecorator(
    `ideas-status-${community_id}-${derived_type_id || ''}`,
    () => swymFetch
      .get(API.Idea.Swym.Statuses(community_id), {
        ...(derived_type_id
          ? { searchParams: [['derived_type_id', derived_type_id]] }
          : undefined),
      })
      .json<SwymResponse<IIdeaStatus[]>>(),
    60000,
  )
}

export function joinCommunity(community_id: string, join_request: string) {
  return swymFetch
    .post('/api/notification/joincommunity', {
      json: {
        params: {
          community_access_wish: join_request,
          related_model_id: community_id,
        },
      },
    })
    .json<SwymResponse<{ success: string }>>()
}

export function searchCommunities(
  params: {
    query: string
    favorites: 'with' | 'without' | 'only'
    hidden?: 'with' | 'without' | 'only'
    my_community: boolean
    creation_granted_for?: IContentModel[]
    anyContentType?: boolean
    select_predicate?: string[] | undefined
  } = {
    query: '#all',
    select_predicate: ['title', 'id', 'resourceid'],
    favorites: 'with',
    my_community: false,
  },
  page = 0,
  limit = 20,
) {
  return swymFetch
    .post('/api/exalead/search', {
      json: {
        params: {
          favorite_communities: params.favorites,
          hidden_communities: params.hidden,
          select_predicate: params.select_predicate,
          start: page * limit,
          query: params.query || '#all',
          myCommunitiesOnly: params.my_community,
          with_synthesis: false,
          creation_granted_for: params.creation_granted_for,
          contentType: 'community',
          nresults: limit,
          anyContentType: params.anyContentType || false,
        },
      },
    })
    .json<ExaleadResponse>()
    .then(response => extractContents(response))
}

export function searchCommunitiesForForward(
  params: { query: string, favorites: 'with' | 'without' | 'only', my_community: boolean, forward_rights_for: string, anyContentType?: boolean },
  page = 0,
  limit = 20,
) {
  return swymFetch
    .post('/api/exalead/search', {
      json: {
        params: {
          favorite_communities: params.favorites,
          select_predicate: ['title', 'id', 'resourceid'],
          start: page * limit,
          query: params.query || '#all',
          myCommunitiesOnly: params.my_community,
          with_synthesis: false,
          forward_rights_for: params.forward_rights_for,
          contentType: 'community',
          nresults: limit,
          anyContentType: params.anyContentType || false,
        },
      },
    })
    .json<ExaleadResponse>()
    .then(response => extractContents(response))
}

export function searchCommunitiesForRiutals(
  params: { query: string, favorites: 'with' | 'without' | 'only', my_community: boolean },
  page = 0,
  limit = 20,
) {
  return swymFetch
    .post('/api/exalead/search', {
      json: {
        params: {
          favorite_communities: params.favorites,
          select_predicate: ['title', 'id', 'resourceid'],
          start: page * limit,
          query: params.query || '#all',
          myCommunitiesOnly: params.my_community,
          creation_granted_for: ['conference'],
          with_synthesis: false,
          contentType: 'community',
          nresults: limit,
        },
      },
    })
    .json<ExaleadResponse>()
    .then(response => extractContents(response))
}

// To get all the derived types of a community
export function fetchDerivedTypes(communityId: ICommunity['id']) {
  return cacheDecorator(
    `derived-types-${communityId}`,
    () => swymFetch
      .get(`/api/v2/communities/${communityId}/derivedtypes`)
      .json<SwymResponse<IDerivedTypeResponse[]>>(),
    60000,
  )
}

export function getUnfollowedCommunities() {
  return swymFetch
    .post('/api/userPreferences/getmyblacklist', {
      json: {
        params: {
          model: 'Community', // Required by API in this format.
        },
      },
    })
    .json<SwymResponse<IUnfollowedCommunity[]>>()
}

export function getCommunityTermsOfUse(communityUri: SUBJECT_6W_URI): Promise<TermsOfUseInfos> {
  return swymFetch
    .get(`/api/v2/communities/${communityUri}/termsofuse`)
    .json<{ result: TermsOfUseInfos }>()
    .then((response) => {
      return {
        filename: response?.result?.filename,
        message: response?.result?.message,
      }
    })
}

export function deleteCommunityTermsOfUse(communityUri: SUBJECT_6W_URI) {
  return swymFetch
    .delete(`/api/v2/communities/${communityUri}/termsofuse`)
    .json()
}

interface SetCommunityTermsOfUseOptions {
  communityUri: SUBJECT_6W_URI
  message: string
  file: File
}

export function setCommunityTermsOfUse(options: SetCommunityTermsOfUseOptions) {
  const formData = new FormData()
  formData.append('file', options.file)

  return swymFetch
    .post(`/api/v2/communities/${options.communityUri}/termsofuse`, {
      headers: { 'content-type': undefined },
      searchParams: {
        message: options.message,
      },
      body: formData,
    })
    .json()
}

interface UpdateCommunityTermsOfUseMessageOptions {
  communityUri: SUBJECT_6W_URI
  message: string
}

export function updateCommunityTermsOfUseMessage(options: UpdateCommunityTermsOfUseMessageOptions) {
  return swymFetch
    .put(`/api/v2/communities/${options.communityUri}/termsofuse`, {
      json: {
        message: options.message,
      },
    })
    .json()
}

export function signCommunityTermsOfUse(communityUri: SUBJECT_6W_URI) {
  return swymFetch
    .post(`/api/v2/communities/${communityUri}/termsofuse/sign`)
    .json()
}

interface GetCommunityTermsOfUseFileOptions {
  communityUri: SUBJECT_6W_URI
  filename: string
}

export function getCommunityTermsOfUseFile(options: GetCommunityTermsOfUseFileOptions): Promise<File> {
  return swymFetch
    .get(`/api/v2/communities/${options.communityUri}/termsofuse/file`)
    .then(async (response) => {
      const blob = await response.blob()
      const type = response.headers.get('Content-Type') || 'application/pdf;charset=UTF-8'
      return new File([blob], options.filename, { type })
    })
}

interface CommunityTermsOfUseFileDetails {
  /** The message added to the terms of use */
  message: string
  /** The name of the file */
  filename: string
  /** Owner of the community */
  owner: Pick<IUser, 'login' | 'first_name' | 'last_name' | 'user_state'> & { 'ds6w:resourceUid': string }
  /** Partial details of the community */
  community: Pick<ICommunity, 'id' | 'title'> & { subject6wuri: string }
  /** The creation date of the file */
  creation_date: string
  /** The modification date of the file */
  modification_date: string
  /** The download url (can be used for native apps) */
  download_url: string
}

/**
 * Retrieves the details of the community terms of use file
 *
 * @param communityUri The subject uri of the community
 * @returns The details object
 */
export function getCommunityTermsOfUseDetails(communityUri: string): Promise<CommunityTermsOfUseFileDetails> {
  return swymFetch
    .get(`/api/v2/communities/${communityUri}/termsofuse/url`)
    .json<SwymResponse<CommunityTermsOfUseFileDetails>>()
    .then(response => response.result)
}

export function getCommunityTermsOfUseSignature(communityUri: SUBJECT_6W_URI): Promise<boolean> {
  return swymFetch
    .get(`/api/v2/communities/${communityUri}/termsofuse/sign`)
    .json<{ result: unknown }>()
    .then(response => !!response?.result)
}
